import React, { useState } from "react";
import type { LanguageView } from "../api.js";
import { discardWording } from "./languages.js";

/**
 * Discards a script in review or a failed language, after asking in place:
 * the button turns into the question and a pair of answers, so nothing is
 * lost to one stray click and no browser dialog takes over the page.
 */
export const DiscardButton: React.FC<{
  language: Pick<LanguageView, "status" | "downloads" | "heldUsd">;
  disabled?: boolean;
  /** Does the discarding, and shows any error itself. */
  onDiscard: () => Promise<void>;
  /** Extra classes, for where it sits in its row or footer. */
  className?: string;
}> = ({ language, disabled = false, onDiscard, className = "" }) => {
  const [asking, setAsking] = useState(false);
  const [working, setWorking] = useState(false);
  const wording = discardWording(language);

  if (!asking) {
    return (
      <button
        type="button"
        className={`btn btn-ghost btn-sm text-ink-300 ${className}`}
        disabled={disabled}
        onClick={() => setAsking(true)}
      >
        {wording.action}
      </button>
    );
  }

  return (
    <div role="group" aria-label={wording.action} className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="text-[12.5px] text-ink-200">{wording.confirm}</span>
      <button
        type="button"
        className="btn btn-danger btn-sm"
        disabled={working}
        onClick={() => {
          setWorking(true);
          void onDiscard()
            .catch(() => undefined)
            .finally(() => {
              setWorking(false);
              setAsking(false);
            });
        }}
      >
        {working ? "Discarding…" : wording.action}
      </button>
      <button type="button" className="btn btn-ghost btn-sm" disabled={working} onClick={() => setAsking(false)}>
        Keep it
      </button>
    </div>
  );
};
